import { forwardRef, ForwardedRef, useImperativeHandle, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  INTERVIEW_TYPE,
  INTERVIEW_TYPE_STR,
  InterviewEntity,
} from 'src/store/interviewApi';
import { selectSortedInterviews } from 'src/store/interviewSlice';
import { selectOptions } from 'src/store/optionSlice';
import { Panel } from 'src/ui/Panel';
import { Badge } from 'src/ui/Badge';
import { Icon } from 'src/ui/Icon';

export interface ContentProps {
  setSelectedInterview: (interview: InterviewEntity) => void;
}

export interface ContentRef {
  scrollTo: (id: number) => void;
}

const TYPE_BADGE_COLOR: Record<number, string> = {
  [INTERVIEW_TYPE.AI]: 'purple',
  [INTERVIEW_TYPE.SINGLE_SELECTION]: 'blue',
  [INTERVIEW_TYPE.MULTIPLE_SELECTION]: 'green',
  [INTERVIEW_TYPE.DROPDOWN]: 'amber',
  [INTERVIEW_TYPE.RANGE_SELECTION]: 'pink',
  [INTERVIEW_TYPE.DATE]: 'gray',
  [INTERVIEW_TYPE.CONFIRM]: 'gray',
  [INTERVIEW_TYPE.LOCATION]: 'gray',
};

const Content = forwardRef(
  ({ setSelectedInterview }: ContentProps, ref: ForwardedRef<ContentRef>) => {
    const optionEntities = useSelector(selectOptions);
    const orderedInterviews = useSelector(selectSortedInterviews);
    const contentRef = useRef<Record<number, HTMLDivElement | null>>({});
    const language = 'cn';

    useImperativeHandle(ref, () => ({
      scrollTo: (id: number) => {
        if (contentRef.current[id]) {
          contentRef.current[id]?.scrollIntoView();
        }
      },
    }));

    const renderAi = (interview: InterviewEntity) => {
      const prefills = interview.prefillAnswers;
      const collects = interview.collect;
      return (
        <div className="col gap-1" style={{ fontSize: 'var(--fs-xs)' }}>
          {prefills?.map((v, idx) => (
            <span key={idx} className="text-muted">
              [Prefill] {v[language]}
            </span>
          ))}
          {collects?.map(([k, v]) => (
            <span key={k} className="text-muted">
              [Collect] <strong>{k}</strong>: {v?.[language]}
            </span>
          ))}
        </div>
      );
    };

    const renderSelection = (interview: InterviewEntity) => {
      if (!interview.optionId) return null;
      const selectedOption = optionEntities[interview.optionId];
      return (
        <div className="col gap-1" style={{ fontSize: 'var(--fs-xs)' }}>
          <span className="text-muted">
            [Collect] <strong>{selectedOption?.field}</strong>
          </span>
          {selectedOption?.labels.map(([key, label]) => (
            <span key={key} className="text-muted">
              {key}: {label[language]}
            </span>
          ))}
        </div>
      );
    };

    const renderDropdownAndRange = (
      interview: InterviewEntity,
      isRange: boolean
    ) => {
      if (!interview.optionId) return null;
      const selectedOption = optionEntities[interview.optionId];
      if (!selectedOption) return null;
      const [item0, item1] = selectedOption.labels || [];
      const itemLast = selectedOption.labels[selectedOption.labels.length - 1];
      let collect = selectedOption.field;
      if (isRange) {
        collect += `Min - ${selectedOption.field}Max`;
      }
      return (
        <div className="col gap-1" style={{ fontSize: 'var(--fs-xs)' }}>
          <span className="text-muted">
            [Collect] <strong>{collect}</strong>
          </span>
          <span className="text-muted">
            {item0[0]}: {item0[1][language]}
          </span>
          <span className="text-muted">
            {item1[0]}: {item1[1][language]}
          </span>
          <span className="text-muted">…</span>
          <span className="text-muted">
            {itemLast[0]}: {itemLast[1][language]}
          </span>
        </div>
      );
    };

    const renderDetail = (interview: InterviewEntity) => {
      switch (interview.type) {
        case INTERVIEW_TYPE.AI:
          return renderAi(interview);
        case INTERVIEW_TYPE.SINGLE_SELECTION:
        case INTERVIEW_TYPE.MULTIPLE_SELECTION:
          return renderSelection(interview);
        case INTERVIEW_TYPE.DROPDOWN:
          return renderDropdownAndRange(interview, false);
        case INTERVIEW_TYPE.RANGE_SELECTION:
          return renderDropdownAndRange(interview, true);
        default:
          return null;
      }
    };

    return (
      <Panel
        className="grow"
        title="Content"
        icon="list"
        headerRight={
          <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
            {orderedInterviews.length} questions
          </span>
        }
      >
        <div className="col gap-2">
          {orderedInterviews.map((interview) => (
            <div
              ref={(el) => {
                contentRef.current[interview.id] = el;
              }}
              id={String(interview.id)}
              key={interview.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedInterview(interview)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  setSelectedInterview(interview);
              }}
              style={{
                padding: 11,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-canvas)',
                cursor: 'pointer',
              }}
            >
              <div
                className="row"
                style={{
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span className="row gap-2" style={{ minWidth: 0 }}>
                  <span
                    className="mono text-muted"
                    style={{ fontSize: 'var(--fs-2xs)' }}
                  >
                    {String(interview.sequence).padStart(2, '0')}
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: 'var(--fs-sm)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {interview.name}
                  </span>
                </span>
                <Badge
                  color={TYPE_BADGE_COLOR[interview.type] ?? 'gray'}
                  size="sm"
                  leftIcon={
                    interview.type === INTERVIEW_TYPE.AI
                      ? 'sparkles'
                      : undefined
                  }
                >
                  {INTERVIEW_TYPE_STR[interview.type]}
                </Badge>
              </div>
              {interview.question[language] && (
                <div
                  className="row gap-1 text-muted"
                  style={{ fontSize: 'var(--fs-xs)', marginBottom: 4 }}
                >
                  <Icon name="message-circle" size={11} />
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {interview.question[language]}
                  </span>
                </div>
              )}
              {interview.condition.type !== 'always' && (
                <div
                  className="row gap-2"
                  style={{ flexWrap: 'wrap', marginTop: 4 }}
                >
                  <Badge color="purple" size="sm" leftIcon="git-branch">
                    if {interview.condition.field} {interview.condition.type}{' '}
                    {interview.condition.value}
                  </Badge>
                </div>
              )}
              {renderDetail(interview)}
            </div>
          ))}
        </div>
      </Panel>
    );
  }
);

export default Content;
